import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import OpenEye from '../../static/images/signup/Openeye.svg';
import CloseEye from '../../static/images/signup/closeeye.svg';
import { IMAGE_CONSTANT } from '../../constants/app';
import Button from '../../ui-core/Button/Button';
import { doChangePasswordRequest } from '../../store/action/auth';
import { APP_ROUTES, getRoute } from '../../route';

const API_FIELD_MAP = {
  current_password: 'currentPassword',
  new_password: 'password',
  new_password_confirmation: 'confirmPassword',
};

const applyPasswordChangeApiErrors = (
  error,
  setFieldError,
  setFieldTouched,
) => {
  const { message, errors } = error?.response?.data || {};
  const fieldErrors = errors && typeof errors === 'object' ? errors : {};

  Object.entries(fieldErrors).forEach(([key, messages]) => {
    const field = API_FIELD_MAP[key];
    const msg = Array.isArray(messages) ? messages[0] : messages;
    if (field && msg) {
      setFieldError(field, msg);
      setFieldTouched(field, true, false);
    }
  });

  if (!Object.keys(fieldErrors).length && message) {
    setFieldError('currentPassword', message);
    setFieldTouched('currentPassword', true, false);
  }
};

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Please enter your current password.'),
  password: Yup.string()
    .required('Please enter a new password.')
    .matches(
      /^(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/,
      'Password must be at least 8 characters and contain at least one number and one special character.',
    ),
  confirmPassword: Yup.string()
    .required('Please confirm your new password.')
    .oneOf([Yup.ref('password'), null], 'Passwords must match.'),
});

const ChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading } = useSelector(state => state.common);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleBack = () => {
    const url = getRoute(APP_ROUTES.PROFILE_WITH_SUB_ROUTES, {
      pathParam: { section: 'edit-profile' },
    });
    navigate(url);
  };

  const formik = useFormik({
    validationSchema,
    initialValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: (values, { setFieldError, setFieldTouched }) => {
      dispatch(
        doChangePasswordRequest({
          data: {
            current_password: values.currentPassword,
            new_password: values.password,
            new_password_confirmation: values.confirmPassword,
          },
          callback: handleBack,
          onError: error =>
            applyPasswordChangeApiErrors(error, setFieldError, setFieldTouched),
        }),
      );
    },
  });

  const isSubmitDisabled =
    isLoading ||
    !formik.isValid ||
    !formik.dirty ||
    !formik.values.currentPassword;

  const renderPasswordField = ({
    id,
    label,
    placeholder,
    fieldName,
    showValue,
    toggleShow,
  }) => (
    <div className="change-password-field">
      <label className="account-settings-label" htmlFor={id}>
        {label}
      </label>
      <div className="change-password-input-row">
        <input
          id={id}
          type={showValue ? 'text' : 'password'}
          name={fieldName}
          className="change-password-input"
          placeholder={placeholder}
          value={formik.values[fieldName]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <button
          type="button"
          tabIndex={-1}
          className="change-password-toggle"
          onClick={toggleShow}
          aria-label={showValue ? 'Hide password' : 'Show password'}
        >
          <img src={showValue ? OpenEye : CloseEye} alt="" />
        </button>
      </div>
      {formik.touched[fieldName] && formik.errors[fieldName] && (
        <span className="account-settings-error">
          {formik.errors[fieldName]}
        </span>
      )}
    </div>
  );

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <button
          type="button"
          className="change-password-back"
          onClick={handleBack}
        >
          <img
            src={IMAGE_CONSTANT.backIcon}
            alt=""
            className="change-password-back-icon"
          />
          BACK
        </button>

        <h1 className="change-password-title">Change password</h1>

        <form className="change-password-form" onSubmit={formik.handleSubmit}>
          {renderPasswordField({
            id: 'current-password',
            label: 'CURRENT PASSWORD',
            placeholder: 'Enter current password',
            fieldName: 'currentPassword',
            showValue: showCurrentPassword,
            toggleShow: () => setShowCurrentPassword(prev => !prev),
          })}

          {renderPasswordField({
            id: 'new-password',
            label: 'NEW PASSWORD',
            placeholder: 'Enter new password',
            fieldName: 'password',
            showValue: showPassword,
            toggleShow: () => setShowPassword(prev => !prev),
          })}

          {renderPasswordField({
            id: 'confirm-password',
            label: 'CONFIRM NEW PASSWORD',
            placeholder: 'Enter confirm password',
            fieldName: 'confirmPassword',
            showValue: showConfirmPassword,
            toggleShow: () => setShowConfirmPassword(prev => !prev),
          })}

          <p className="change-password-hint">
            Your password should have at least 8 characters, one number, and one
            special character.
          </p>

          <Button
            class="change-password-submit-btn"
            text="SUBMIT"
            type="submit"
            isDisabled={isSubmitDisabled}
            isLoading={isLoading}
          />
        </form>
      </div>
    </div>
  );
};

ChangePassword.propTypes = {};

export default ChangePassword;
